<template>
  <el-dialog
    :title="material ? '编辑素材' : '添加素材'"
    v-model="dialogVisible"
    width="700px"
    :before-close="handleClose"
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="100px"
    >
      <el-form-item label="标题" prop="title">
        <el-input 
          v-model="form.title" 
          placeholder="请输入标题"
          :disabled="titleFromFile"
        />
        <div class="form-tip">
          <el-checkbox v-model="titleFromFile">从FLA文件名获取标题</el-checkbox>
        </div>
      </el-form-item>

      <el-form-item label="分类" prop="category">
        <el-cascader
          v-model="form.category"
          :options="formattedCategories"
          :props="{
            checkStrictly: false,
            label: 'name',
            value: 'id',
            children: 'children'
          }"
          placeholder="请选择分类"
        />
      </el-form-item>

      <el-form-item label="描述" prop="description">
        <el-input
          v-model="form.description"
          type="textarea"
          :rows="3"
          placeholder="请输入素材描述"
        />
      </el-form-item>

      <el-form-item label="预览图" prop="preview">
        <el-upload
          class="material-uploader"
          action="#"
          :show-file-list="false"
          :headers="uploadHeaders"
          :auto-upload="true"
          :http-request="getUploadUrlV2('preview')"
          :before-upload="beforePreviewUpload"
          :on-success="handlePreviewSuccess"
          :on-error="handleUploadError"
          :on-exceed="handleExceed"
          accept="image/*"
          :limit="1"
        >
          <img
            v-if="form.preview"
            :src="previewImageUrl"
            class="preview-image"
            @error="handleImageError"
          />
          <el-icon v-else class="upload-icon"><Plus /></el-icon>
        </el-upload>
        <div class="form-tip">支持jpg、png、gif格式，建议尺寸1920x1080px</div>
      </el-form-item>

      <el-form-item label="FLA文件" prop="fla">
        <input
          type="file"
          ref="flaFileInput"
          style="display: none"
          accept=".fla"
          @change="onFlaFileChange"
        />
        <div class="custom-upload" @click="$refs.flaFileInput.click()">
          <template v-if="form.fla">
            <div class="file-preview">
              <span class="file-name">
                {{ localFlaName || form.fla.split('/').pop() || '动画文件.fla' }}
              </span>
              <el-icon class="replace-icon"><RefreshRight /></el-icon>
            </div>
          </template>
          <template v-else>
            <div class="upload-btn">
              <el-icon class="upload-icon"><Plus /></el-icon>
              <span>上传FLA文件</span>
            </div>
          </template>
        </div>
      </el-form-item>

      <el-form-item label="PSD文件" prop="psd">
        <el-upload
          class="material-uploader"
          :show-file-list="false"
          action="#"
          :auto-upload="true"
          :headers="uploadHeaders"
          :http-request="getUploadUrlV2('psd')"
          :before-upload="beforePsdUpload"
          :on-success="handlePsdSuccess"
          :on-error="handleUploadError"
          accept=".psd"
          :limit="1"
        >
          <div v-if="form.psd" class="file-preview psd">
            <span class="file-name">
              {{ getSimpleFileName(form.psd, 'psd') }}
            </span>
            <el-icon class="replace-icon"><RefreshRight /></el-icon>
          </div>
          <el-icon v-else class="upload-icon"><Plus /></el-icon>
        </el-upload>
      </el-form-item>

      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="form.status">
          <el-radio label="active">启用</el-radio>
          <el-radio label="inactive">禁用</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="loading">
          确定
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios';
import { Plus, RefreshRight } from '@element-plus/icons-vue'
import type { FormInstance, FormRules } from 'element-plus'

// 定义分类相关的类型
interface Category {
  id: number
  name: string
  code: string
  type: string
  parent_id: number | null
  children?: Category[]
}

interface MaterialForm {
  title: string
  category: number[] | null
  category_id?: number
  description: string
  preview: string
  fla: string
  psd: string
  status: 'active' | 'inactive'
}

// 定义文件名映射的类型
interface FileNameMap {
  [key: string]: string
}

const props = defineProps<{
  visible: boolean
  material: any | null
  categories: Category[]
}>()

const emit = defineEmits(['update:visible', 'success'])

const dialogVisible = ref(props.visible)
const loading = ref(false)
const formRef = ref<FormInstance>()
const titleFromFile = ref(true)

// 表单数据
const form = ref<MaterialForm>({
  title: '',
  category: null,
  description: '',
  preview: '',
  fla: '',
  psd: '',
  status: 'active'
})

// 表单规则
const rules = ref<FormRules>({
  title: [
    { required: true, message: '请输入标题', trigger: 'blur' }
  ],
  category: [
    { required: true, message: '请选择分类', trigger: 'change' }
  ],
  preview: [
    { required: true, message: '请上传预览图', trigger: 'change' }
  ]
})

// 存储原始文件名映射
const fileNameMap = ref({})

// 添加这些变量用于存储原始文件名
const flaFileName = ref('');
const psdFileName = ref('');

// 添加文件名本地存储变量
const uploadedFiles = ref({
  fla: { path: '', originalName: '' },
  psd: { path: '', originalName: '' }
});

// 添加变量存储真实文件名
const flaOriginalName = ref('');
const psdOriginalName = ref('');

// 存储本地文件名
const localFlaName = ref('');
const localPsdName = ref('');

// 监听 material 属性变化
watch(() => props.material, (newMaterial) => {
  if (newMaterial) {
    // 填充表单数据
    form.value = {
      title: newMaterial.title || '',
      category: newMaterial.category || null, // 这里使用处理过的分类路径
      description: newMaterial.description || '',
      preview: newMaterial.preview_image || '',
      fla: newMaterial.fla_file || '',
      psd: newMaterial.psd_file || '',
      status: newMaterial.status || 'active'
    }
    titleFromFile.value = false

    // 重置文件名映射
    fileNameMap.value = {}

    // 重置文件名
    flaFileName.value = '';
    psdFileName.value = '';

    // 编辑时尝试提取文件名
    if (newMaterial.fla_file && typeof newMaterial.fla_file === 'string') {
      flaFileName.value = newMaterial.fla_file.split('/').pop() || 'animation.fla';
    }

    if (newMaterial.psd_file && typeof newMaterial.psd_file === 'string') {
      psdFileName.value = newMaterial.psd_file.split('/').pop() || 'design.psd';
    }

    console.log('编辑素材，初始化文件名映射:', fileNameMap.value);
  } else {
    // 重置表单时也清空文件名映射
    fileNameMap.value = {}
    // 重置所有值
    flaFileName.value = '';
    psdFileName.value = '';
  }
}, { immediate: true })

// 获取上传URL
const getUploadUrl = (type: string) => {
  // 获取当前选择的分类代码
  if (!form.value.category || !Array.isArray(form.value.category) || form.value.category.length === 0) {
    return `/api/upload?type=${type}`;
  }
  
  // 获取选择的分类ID（可能是主分类ID或子分类ID）
  let categoryId: number;
  let parentCategoryId: number | null = null;
  
  if (form.value.category.length > 1) {
    // 如果选择了子分类，获取父分类ID和子分类ID
    parentCategoryId = form.value.category[0];
    categoryId = form.value.category[form.value.category.length - 1];
  } else {
    // 如果只选择了主分类，只获取主分类ID
    categoryId = form.value.category[0];
  }
  
  // 查找分类代码
  let categoryCode = '';
  let isSubCategory = false;
  
  // 查找当前分类
  for (const mainCategory of props.categories) {
    // 检查是否是主分类
    if (mainCategory.id === categoryId) {
      categoryCode = mainCategory.code;
      break;
    }
    
    // 检查是否是子分类
    if (mainCategory.children) {
      const subCategory = mainCategory.children.find(sub => sub.id === categoryId);
      if (subCategory) {
        categoryCode = subCategory.code;
        isSubCategory = true;
        break;
      }
    }
  }
  
  if (!categoryCode) {
    return `/api/upload?type=${type}`;
  }
  
  return `/api/upload?type=${type}&category_code=${categoryCode}`;
};



// 获取上传URL上传oss
const getUploadUrlV2 = (type: string) => {
  return async (options) => {
    const { file } = options;
    console.log('上传文件', file)
    try {
      // 1. 获取预签名 URL
      const response = await fetch('/api/upload/getUoloadUrl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          objectName: `${type}/${form.value.title}`,
        }),
      });

      if (!response.ok) throw new Error('获取上传URL失败');

      const { data } = await response.json();
      const { url } = data;

      // 2. 上传文件到 OSS
      await uploadFile(file, url);

      // 3. 通知 el-upload 上传成功
      options.onSuccess({ url });
    } catch (error) {
      // 4. 通知 el-upload 上传失败
      options.onError(error);
    }
  };
};

const uploadFile = async (file: File, presignedUrl: string) => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': 'application/octet-stream', // 必须与预签名 URL 的 contentType 一致
    },
    mode: 'cors'
  });
  if (!response.ok) {
    throw new Error(`Upload failed, status: ${response.status}`);
  }
};



// 上传请求头
const uploadHeaders = computed(() => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`
}))

// 格式化分类数据为树形结构
const formattedCategories = computed(() => {
  return props.categories.map(parent => ({
    id: parent.id,
    name: parent.name,
    code: parent.code,
    type: parent.type,
    children: parent.children?.map(child => ({
      id: child.id,
      name: child.name,
      code: child.code,
      type: child.type
    }))
  }));
});

// 计算预览图URL
const previewImageUrl = computed(() => {
  if (!form.value.preview) return '';
  
  // 确保使用正斜杠
  const normalizedPath = form.value.preview.replace(/\\/g, '/');
  
  // 如果是完整URL，直接返回
  if (normalizedPath.startsWith('http')) return normalizedPath;
  
  // 如果已经以/materials开头，直接返回
  if (normalizedPath.startsWith('/materials')) return normalizedPath;
  
  // 否则添加/materials前缀
  return `/materials/${normalizedPath.replace(/^\//, '')}`;
});

// 监听visible变化
watch(() => props.visible, (val) => {
  dialogVisible.value = val
})

watch(() => dialogVisible.value, (val) => {
  emit('update:visible', val)
})

// 监听分类选择变化
watch(() => form.value.category, (newVal) => {
  if (newVal && Array.isArray(newVal)) {
    // 如果选择了二级分类，使用二级分类的ID
    form.value.category_id = newVal[newVal.length - 1];
  }
}, { deep: true });

// 修改文件上传前的验证
const beforeUpload = (file: File) => {
  // 检查是否选择了分类
  if (!form.value.category || !Array.isArray(form.value.category)) {
    ElMessage.error('请先选择分类！');
    return false;
  }

  // 添加调试日志
  console.log('Before upload check:', {
    category: form.value.category,
    uploadUrl: getUploadUrl(file.name.toLowerCase().endsWith('.fla') ? 'fla' : 
               file.name.toLowerCase().endsWith('.psd') ? 'psd' : 'preview')
  });

  // 根据上传字段进行不同的验证
  const fieldName = file.name.toLowerCase();
  
  if (fieldName.endsWith('.fla')) {
    // FLA文件验证
    return true;
  } else if (fieldName.endsWith('.psd')) {
    // PSD文件验证
    return true;
  } else {
    // 预览图验证
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      ElMessage.error('预览图只能上传图片文件!');
      return false;
    }
  }

  // 检查文件大小（限制为100MB）
  const isLt100M = file.size / 1024 / 1024 < 100;
  if (!isLt100M) {
    ElMessage.error('文件大小不能超过100MB!');
    return false;
  }

  return true;
}

// 添加超出限制处理函数
const handleExceed = (files: File[], uploadFiles: File[]) => {
  // 如果用户已有预览图并想要更换
  if (files.length > 0) {
    // 获取要上传的文件
    const file = files[0];
    
    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      ElMessage.error('预览图只能上传图片文件!');
      return;
    }

    // 检查文件大小（限制为10MB）
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      ElMessage.error('预览图大小不能超过10MB!');
      return;
    }
    
    // 使用FormData直接上传文件，而不是触发input事件
    const formData = new FormData();
    formData.append('file', file);
    
    // 显示上传中的提示
    const loadingInstance = ElMessage.info({
      message: '正在上传预览图...',
      duration: 0
    });
    
    // 获取上传URL，与getUploadUrl保持一致
    const uploadUrl = getUploadUrl('preview');
    
    // 直接发送上传请求
    fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    })
    .then(response => response.json())
    .then(result => {
      // 关闭加载提示
      loadingInstance.close();
      
      if (result.success) {
        // 更新预览图URL
        form.value.preview = result.url;
        ElMessage.success('预览图更新成功');
      } else {
        ElMessage.error(result.message || '预览图上传失败');
      }
    })
    .catch(error => {
      // 关闭加载提示
      loadingInstance.close();
      console.error('预览图上传错误:', error);
      ElMessage.error('预览图上传失败');
    });
  } else {
    ElMessage.warning('请选择一张图片');
  }
}

// 添加预览图上传前的验证函数
const beforePreviewUpload = (file: File) => {
  // 检查是否选择了分类
  if (!form.value.title) {
    ElMessage.error('请先填写标题！');
    return false;
  }

  // 检查是否选择了分类
  if (!form.value.category || !Array.isArray(form.value.category)) {
    ElMessage.error('请先选择分类！');
    return false;
  }
  
  // 检查文件类型
  const isImage = file.type.startsWith('image/');
  if (!isImage) {
    ElMessage.error('预览图只能上传图片文件!');
    return false;
  }
  
  // 检查文件大小（限制为10MB）
  const isLt10M = file.size / 1024 / 1024 < 10;
  if (!isLt10M) {
    ElMessage.error('预览图大小不能超过10MB!');
    return false;
  }
  
  return true;
}

// 添加PSD文件上传前的验证函数
const beforePsdUpload = (file: File) => {
  // 检查是否选择了分类
  if (!form.value.category || !Array.isArray(form.value.category)) {
    ElMessage.error('请先选择分类！');
    return false;
  }
  
  // 检查文件扩展名
  const isPsd = file.name.toLowerCase().endsWith('.psd');
  if (!isPsd) {
    ElMessage.error('只能上传PSD文件!');
    return false;
  }
  
  // 检查文件大小（限制为100MB）
  const isLt100M = file.size / 1024 / 1024 < 100;
  if (!isLt100M) {
    ElMessage.error('文件大小不能超过100MB!');
    return false;
  }
  
  return true;
}

// 修改预览图上传成功处理函数
const handlePreviewSuccess = (response: any) => {
  if (response.success) {
    // 如果已经有预览图，先删除旧的
    if (form.value.preview) {
      // TODO: 可以添加删除旧文件的API调用
      console.log('Should delete old preview:', form.value.preview);
    }
    
    // 保存新的预览图URL
    form.value.preview = response.url;
    
    console.log('Preview upload success:', {
      response,
      previewUrl: form.value.preview,
      computedUrl: previewImageUrl.value
    });
  } else {
    ElMessage.error('预览图上传失败');
  }
};

// 处理图片加载错误
const handleImageError = () => {
  console.error('Image load error:', {
    preview: form.value.preview,
    computedUrl: previewImageUrl.value,
    fullUrl: window.location.origin + previewImageUrl.value
  });
  
  // 尝试检查文件是否存在
  fetch(previewImageUrl.value)
    .then(response => {
      console.log('File check response:', response);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    })
    .catch(error => {
      console.error('File check failed:', error);
    });
};

// 修改处理FLA文件上传成功的方法
const handleFlaSuccess = (response) => {
  console.log('FLA上传成功:', response);
  
  // 保存文件路径
  form.value.fla = response.url;
  
  // 使用返回的原始文件名或者本地存储的文件名
  if (flaOriginalName.value) {
    console.log('使用本地存储的文件名:', flaOriginalName.value);
  } else if (response.originalName) {
    flaOriginalName.value = response.originalName;
    console.log('使用服务器返回的文件名:', response.originalName);
  }
  
  // 如果设置了从文件名获取标题
  if (titleFromFile.value && flaOriginalName.value) {
    form.value.title = flaOriginalName.value.replace(/\.fla$/i, '');
  }
  
  ElMessage.success('FLA文件上传成功');
};

// 修改处理PSD文件上传成功的方法
const handlePsdSuccess = (response: any) => {
  console.log('PSD上传响应:', response);
  
  // 保存PSD文件路径
  form.value.psd = response.url;
  
  // 直接保存原始文件名
  psdFileName.value = response.originalName || 'design.psd';
  
  ElMessage.success('PSD文件上传成功');
};

// 处理上传错误
const handleUploadError = (error: any) => {
  console.error('Upload error:', error);
  let errorMessage = '上传失败';
  
  try {
    if (typeof error.message === 'string') {
      const errorData = JSON.parse(error.message);
      errorMessage = errorData.message || errorData.error || '上传失败';
    }
  } catch {
    errorMessage = error.message || '上传失败';
  }
  
  ElMessage.error(errorMessage);
};

// 获取文件名函数
const getFileName = (path: string) => {
  console.log('获取文件名函数调用:', { path, map: fileNameMap.value });
  
  if (!path) return '';
  
  // 尝试从映射中获取原始名称
  if (fileNameMap.value[path]) {
    return fileNameMap.value[path];
  }
  
  // 尝试从路径中提取文件名
  const extracted = path.split('/').pop() || '';
  console.log('从路径提取的文件名:', extracted);
  return extracted;
};

// 处理关闭
const handleClose = () => {
  dialogVisible.value = false
}

// 一个更简单的方法直接设置type
const getCategoryType = (categoryId: number) => {
  if (!categoryId) return 'character';  // 默认为人物素材
  
  // 根据一级分类ID范围判断类型
  if (categoryId === 1 || (categoryId >= 7 && categoryId <= 12)) {
    return 'character';  // 人物素材
  } else if (categoryId === 2 || (categoryId >= 13 && categoryId <= 16)) {
    return 'scene';      // 场景素材
  } else if (categoryId === 3 || (categoryId >= 17 && categoryId <= 18)) {
    return 'expression'; // 表情素材
  } else if (categoryId === 4 || (categoryId >= 19 && categoryId <= 20)) {
    return 'creature';   // 兽类素材
  } else if (categoryId === 5 || (categoryId >= 21 && categoryId <= 25)) {
    return 'prop';       // 道具素材
  } else if (categoryId === 6) {
    return 'effect';     // 特效素材
  } else if (categoryId === 7) {
    return 'ai';         // AI素材
  }
  
  return 'character';    // 默认为人物素材
};

// 修改提交处理函数
const handleSubmit = async () => {
  if (!formRef.value) return;
  
  try {
    await formRef.value.validate();
    loading.value = true;

    // 获取分类ID
    const categoryId = Array.isArray(form.value.category) 
      ? form.value.category[form.value.category.length - 1] 
      : form.value.category;
    
    // 获取父分类ID
    const parentCategoryId = Array.isArray(form.value.category) && form.value.category.length > 1
      ? form.value.category[0]
      : null;

    // 获取分类类型
    let materialType = getCategoryType(categoryId);

    // 构建请求数据，添加type字段
    const requestData = {
      title: form.value.title,
      category_id: categoryId,
      parent_category_id: parentCategoryId,
      description: form.value.description || '',
      preview_url: form.value.preview,
      fla_url: form.value.fla,
      psd_url: form.value.psd,
      status: form.value.status,
      type: materialType
    };

    console.log('Submitting material:', requestData);

    const response = await fetch('/api/materials', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(requestData)
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || '添加失败');
    }

    if (!result.success) {
      throw new Error(result.message || '添加失败');
    }

    ElMessage.success('添加成功');
    dialogVisible.value = false;
    emit('success');
  } catch (error: any) {
    console.error('添加素材失败:', error);
    ElMessage.error(error.message || '添加失败');
  } finally {
    loading.value = false;
  }
};

// 修改获取文件名的函数
const getActualFileName = (path, type) => {
  if (type === 'fla' && uploadedFiles.value.fla.path === path) {
    return uploadedFiles.value.fla.originalName;
  }
  
  if (type === 'psd' && uploadedFiles.value.psd.path === path) {
    return uploadedFiles.value.psd.originalName;
  }
  
  // 后备方案：使用生成的文件名
  return getSimpleFileName(path, type);
};

// 修改getSimpleFileName函数
const getSimpleFileName = (path, type) => {
  if (!path) return '';
  
  // 优先使用本地保存的原始文件名
  if (type === 'fla' && flaOriginalName.value) {
    return flaOriginalName.value;
  }
  
  if (type === 'psd' && psdOriginalName.value) {
    return psdOriginalName.value;
  }
  
  // 后备方案：从路径提取文件名
  const filename = path.split('/').pop() || '';
  
  // 如果是以时间戳命名的文件，提供更友好的名称
  if (/^\d+-[a-z0-9]+\.(fla|psd)$/i.test(filename)) {
    if (type === 'fla') return 'animation.fla';
    if (type === 'psd') return 'design.psd';
  }
  
  return filename;
};

// 处理FLA文件选择
const onFlaFileChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;
  
  console.log('选择了文件:', file.name);
  
  // 立即保存本地文件名
  localFlaName.value = file.name;
  
  // 如果设置了从文件名获取标题
  if (titleFromFile.value) {
    form.value.title = file.name.replace(/\.fla$/i, '');
  }
  
  // 执行文件上传
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    loading.value = true;
    
    // 使用getUploadUrl方法获取上传路径
    const uploadUrl = getUploadUrl('fla');
    
    // 手动上传文件
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    });
    
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.message || '上传失败');
    }
    
    // 保存文件路径
    form.value.fla = result.url;
    ElMessage.success('FLA文件上传成功');
  } catch (error) {
    console.error('上传文件失败:', error);
    ElMessage.error(error.message || '上传失败');
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.material-uploader {
  width: 200px;
}

.preview-image {
  width: 200px;
  height: 112px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
}

.upload-icon {
  width: 200px;
  height: 112px;
  font-size: 28px;
  color: #8c939d;
  text-align: center;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-icon:hover {
  border-color: var(--el-color-primary);
  color: var(--el-color-primary);
}

.custom-upload {
  width: 200px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
}

.upload-btn {
  width: 100%;
  height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  padding: 0;
}

.upload-btn .upload-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.file-preview {
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  cursor: pointer;
  position: relative;
  border: none;
}

.file-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 24px;
  font-size: 13px;
}

.replace-icon {
  position: absolute;
  right: 12px;
  opacity: 0;
  transition: opacity 0.3s;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.debug-info {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  word-break: break-all;
}
</style> 